import {AnyPcbNode, DocumentPreview, DocumentPreviewDiff, IDocumentData, PcbNodesMap} from "@buildwithflux/models";
import {guid} from "@buildwithflux/shared";
import {diff, revertChange} from "deep-diff";
import {type Draft, create} from "mutative";
import {pick} from "lodash";
import {DocumentStore} from "modules/document/state/document";
import {ActionBakeObserver} from "modules/observers/ActionBakeObserver";

/**
 * Service for managing document previews using a diff-based system.
 *
 * Previews allow temporary modifications to the document that can be rendered,
 * cleared, or applied permanently. The system uses deep-diff patches to track
 * changes and automatically locks affected nodes.
 */
export interface AbstractDocumentPreviewService {
    /**
     * Whether the current document state has an active preview.
     */
    hasActivePreview(): boolean;

    /**
     * Renders a preview by dispatching the preview diff.
     * Returns true if the preview was successfully baked.
     */
    renderPreview(
        mutationFn: (draftState: Draft<IDocumentData>) => void,
        cacheKey?: string,
        awaitDrcCompletion?: boolean,
    ): Promise<boolean>;

    /**
     * Clears the active preview and unlocks all preview nodes.
     * Returns true if the clear operation was successfully baked.
     */
    clearPreview(): Promise<boolean>;

    /**
     * Applies the current preview as a committed change to the document.
     * Clears the preview first, then creates an action record for undo/redo.
     * Returns true if the apply operation was successful.
     */
    applyPreview(mutationFn?: (draftState: Draft<IDocumentData>) => void): Promise<boolean>;

    /**
     * Checks if a node is part of the current preview.
     */
    isPreviewNode(nodeUid: string): boolean;
}

/**
 * Implementation of the document preview service.
 *
 * @example
 * await documentPreviewService.renderPreview((draft) => {
 *     draft.pcbLayoutNodes[nodeUid].position = newPosition;
 * });
 * // ... user reviews the preview
 * await documentPreviewService.applyCurrentPreview();
 */
export class DocumentPreviewService implements AbstractDocumentPreviewService {
    /**
     * Document properties that are included in preview diffs.
     * Add new properties here to extend preview support.
     */
    public static readonly previewDiffProperties = [
        "pcbLayoutNodes",
        "pcbLayoutRuleSets",
    ] as const satisfies (keyof IDocumentData)[];

    private readonly cache: Map<string, DocumentPreview> = new Map();

    constructor(
        private readonly documentStore: DocumentStore,
        private readonly actionBakeObserver: ActionBakeObserver,
    ) {}

    /**
     * Calculate the deep-diff between two document states, focusing on preview-relevant properties.
     */
    public static calculatePreviewDiff(oldDocument: IDocumentData, newDocument: IDocumentData): DocumentPreviewDiff {
        return (
            diff(
                pick(oldDocument, DocumentPreviewService.previewDiffProperties),
                pick(newDocument, DocumentPreviewService.previewDiffProperties),
            ) ?? []
        );
    }

    public hasActivePreview(): boolean {
        const document = this.documentStore.getState().document;
        return document?.preview !== undefined;
    }

    public async renderPreview(
        mutationFn: (draftState: Draft<IDocumentData>) => void,
        cacheKey?: string,
        awaitDrcCompletion = false,
    ): Promise<boolean> {
        const previewKey = cacheKey ?? guid();

        const document = this.documentStore.getState().document;
        if (!document) {
            throw new Error("Cannot create preview diff: no document in state");
        }
        const hasExistingPreview = this.hasExistingPreview(document);

        // Use cached diff if available to avoid expensive diff calculation every time
        if (cacheKey) {
            const cachedPreview = this.cache.get(cacheKey);
            if (cachedPreview) {
                if (this.isEmptyDiff(cachedPreview.diff) && !hasExistingPreview) {
                    return true;
                }

                const {success} = await this.actionBakeObserver.asyncDispatch(
                    (subscriptionKey) => this.documentStore.getState().renderPreview(cachedPreview, subscriptionKey),
                    {awaitDrcCompletion},
                );
                return success;
            }
        }

        // 1. Revert any existing preview to get the base document state
        const baseDocument = this.revertExistingPreview(document);

        // 2. Create the new preview document state
        const previewDocument = create(baseDocument, mutationFn);

        // 3. Calculate the diff between the depreviewed document and the new preview document
        const previewDiff = DocumentPreviewService.calculatePreviewDiff(baseDocument, previewDocument);

        // 4. Determine which nodes were touched by the preview
        const previewNodeUids = this.getTouchedNodeUidsFromDiff(previewDiff, baseDocument.pcbLayoutNodes);

        const preview: DocumentPreview = {
            key: previewKey,
            diff: previewDiff,
            previewNodeUids: previewNodeUids,
        };

        // Cache the preview
        if (cacheKey) {
            this.cache.set(cacheKey, preview);
        }

        // If the diff is empty, no need to dispatch render action
        if (this.isEmptyDiff(previewDiff) && !hasExistingPreview) {
            return true;
        }

        // Dispatch the render preview action and wait for it to be baked
        const {success} = await this.actionBakeObserver.asyncDispatch(
            (subscriptionKey) => this.documentStore.getState().renderPreview(preview, subscriptionKey),
            {awaitDrcCompletion},
        );
        return success;
    }

    public async clearPreview() {
        // Clear the cached diffs
        this.cache.clear();

        // Dispatch the clear preview action and wait for it to be baked
        const {success} = await this.actionBakeObserver.asyncDispatch((subscriptionKey) =>
            this.documentStore.getState().clearPreview(subscriptionKey),
        );

        return success;
    }

    public async applyPreview(customMutationFn?: (draftState: Draft<IDocumentData>) => void): Promise<boolean> {
        const document = this.documentStore.getState().document;
        if (!document) {
            throw new Error("No document in state to apply preview to");
        }

        let previewDiffToApply: DocumentPreviewDiff | undefined;

        if (customMutationFn) {
            previewDiffToApply = DocumentPreviewService.calculatePreviewDiff(
                document,
                create(document, customMutationFn),
            );
        } else if (document.preview) {
            previewDiffToApply = document.preview.diff;
        }

        if (!previewDiffToApply) {
            // Nothing to apply
            return true;
        }

        // Clear the preview state first before applying to avoid persisting locked nodes.
        // If we applied directly, the locked preview nodes would be part of the persisted
        // patch, and undoing the apply would restore them in a locked state.
        await this.clearPreview();

        const {success} = await this.actionBakeObserver.asyncDispatch((subscriptionKey) =>
            this.documentStore.getState().applyPreviewChanges(previewDiffToApply, subscriptionKey),
        );
        return success;
    }

    public isPreviewNode(nodeUid: string): boolean {
        const document = this.documentStore.getState().document;
        if (!document || !document.preview) {
            return false;
        }

        return document.preview.previewNodeUids.includes(nodeUid);
    }

    private revertExistingPreview(document: IDocumentData): IDocumentData {
        if (!document.preview) {
            return document;
        }

        return create(document, (draftState) => {
            if (!draftState.preview) {
                return;
            }

            for (const nodeUid in draftState.preview.initialLockValueMap) {
                const node = draftState.pcbLayoutNodes[nodeUid];
                if (node) {
                    node.locked = draftState.preview.initialLockValueMap[nodeUid];
                }
            }

            for (const change of draftState.preview.diff) {
                revertChange(draftState, document, change);
            }
        });
    }

    private getTouchedNodeUidsFromDiff(
        previewDiff: DocumentPreviewDiff,
        pcbLayoutNodes: PcbNodesMap<AnyPcbNode>,
    ): string[] {
        const touchedNodeUids: Set<string> = new Set();

        for (const change of previewDiff) {
            if (!change.path) {
                continue;
            }

            if (change.path[0] === "pcbLayoutNodes") {
                if (change.path.length >= 2) {
                    const nodeUid = change.path[1];
                    if (nodeUid && typeof nodeUid === "string") {
                        touchedNodeUids.add(nodeUid);
                    }

                    continue;
                }

                // Whole pcbLayoutNodes object changed, so all nodes are considered touched
                if (change.path.length === 1) {
                    for (const nodeUid in pcbLayoutNodes) {
                        touchedNodeUids.add(nodeUid);
                    }
                }
            }
        }

        return Array.from(touchedNodeUids);
    }

    private isEmptyDiff(previewDiff: DocumentPreviewDiff): boolean {
        return previewDiff.length === 0;
    }

    private hasExistingPreview(document: IDocumentData): boolean {
        return document.preview !== undefined;
    }
}
